// Dense data grid for Sprout's lists (interface refresh, screen 6, decision 10A): sticky header,
// numbers right-aligned, sort by clicking a column, a column picker remembered per browser, and
// CSV of what is shown (the visible columns, in the shown order). Rows can open a page, carry a
// tick box, or have a full-width row under them (e.g. a renew form).
import { Fragment, ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { irishDate } from '../format';

type Plain = string | number | null | undefined;

export interface Column<T> {
  key: string;
  label: string;
  // Plain value: used for sorting and CSV unless sortBy / csv are given.
  value: (r: T) => Plain;
  cell?: (r: T) => ReactNode;
  sortBy?: (r: T) => Plain;
  csv?: (r: T) => Plain;
  align?: 'right';
  hidden?: boolean;   // hidden until picked in Columns
  always?: boolean;   // can't be hidden
  nosort?: boolean;
  nowrap?: boolean;
  nocsv?: boolean;    // actions, not data
}

interface Saved { hidden?: string[]; shown?: string[]; sort?: { key: string; dir: 1 | -1 } | null }
const load = (id: string): Saved => { try { return JSON.parse(localStorage.getItem(`ff.grid.${id}`) ?? '{}') as Saved; } catch { return {}; } };
const store = (id: string, s: Saved) => { try { localStorage.setItem(`ff.grid.${id}`, JSON.stringify(s)); } catch { /* private mode */ } };

const cmp = (a: Plain, b: Plain) => {
  const na = a === null || a === undefined || a === '';
  const nb = b === null || b === undefined || b === '';
  if (na || nb) return na === nb ? 0 : na ? 1 : -1;   // empty values last
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  return String(a).localeCompare(String(b), 'en-IE', { numeric: true, sensitivity: 'base' });
};

// CSV cell: quoted when needed; text that a spreadsheet would run as a formula gets a leading '.
function csvCell(v: Plain): string {
  if (v === null || v === undefined) return '';
  if (typeof v === 'number') return String(v);
  const s = /^[=+\-@\t\r]/.test(v) ? `'${v}` : v;
  return /[",\r\n;]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

export function DataGrid<T>({ id, rows, columns, rowKey, noun, href, rowState, select, after, rowClass, defaultSort, empty }: {
  id: string;                         // remembers columns and sort under this name
  rows: T[];
  columns: Column<T>[];
  rowKey: (r: T) => number | string;
  noun: [string, string];             // e.g. ['vehicle', 'vehicles']
  href?: (r: T) => string;            // the row opens this page
  rowState?: unknown;                 // router state passed when a row opens (for "← Back")
  select?: { picked: Set<number>; onChange: (next: Set<number>) => void; label: (r: T) => string };
  after?: (r: T) => ReactNode;        // full-width row under a row
  rowClass?: (r: T) => string | undefined;
  defaultSort?: { key: string; dir: 1 | -1 };
  empty?: ReactNode;
}) {
  const navigate = useNavigate();
  const saved = useMemo(() => load(id), [id]);
  const [hidden, setHidden] = useState<Set<string>>(() => new Set([
    ...columns.filter((c) => c.hidden && !saved.shown?.includes(c.key)).map((c) => c.key),
    ...(saved.hidden ?? []).filter((k) => columns.some((c) => c.key === k && !c.always)),
  ]));
  const [sort, setSort] = useState<{ key: string; dir: 1 | -1 } | null>(saved.sort === undefined ? defaultSort ?? null : saved.sort);
  const [picking, setPicking] = useState(false);
  const pickRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    store(id, {
      hidden: columns.filter((c) => !c.hidden && hidden.has(c.key)).map((c) => c.key),
      shown: columns.filter((c) => c.hidden && !hidden.has(c.key)).map((c) => c.key),
      sort,
    });
  }, [id, columns, hidden, sort]);

  // Close the column picker on a click outside or Escape.
  useEffect(() => {
    if (!picking) return;
    const off = (e: MouseEvent) => { if (!pickRef.current?.contains(e.target as Node)) setPicking(false); };
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') setPicking(false); };
    document.addEventListener('mousedown', off);
    document.addEventListener('keydown', esc);
    return () => { document.removeEventListener('mousedown', off); document.removeEventListener('keydown', esc); };
  }, [picking]);

  const cols = columns.filter((c) => !hidden.has(c.key));
  const sorted = useMemo(() => {
    const col = sort && columns.find((c) => c.key === sort.key);
    if (!col) return rows;
    const by = col.sortBy ?? col.value;
    return [...rows].sort((a, b) => cmp(by(a), by(b)) * sort!.dir);
  }, [rows, columns, sort]);

  const clickSort = (c: Column<T>) => {
    if (c.nosort) return;
    setSort((s) => (s?.key !== c.key ? { key: c.key, dir: c.align === 'right' ? -1 : 1 } : s.dir === 1 ? { key: c.key, dir: -1 } : { key: c.key, dir: 1 }));
  };

  const downloadCsv = () => {
    const out = cols.filter((c) => !c.nocsv);
    const lines = [out.map((c) => csvCell(c.label)).join(','), ...sorted.map((r) => out.map((c) => csvCell((c.csv ?? c.value)(r))).join(','))];
    const url = URL.createObjectURL(new Blob(['﻿' + lines.join('\r\n')], { type: 'text/csv;charset=utf-8' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `${id}-${irishDate()}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  const allPicked = !!select && rows.length > 0 && rows.every((r) => select.picked.has(rowKey(r) as number));
  const colCount = cols.length + (select ? 1 : 0);

  return (
    <div className="grid-box">
      <div className="grid-bar">
        <span className="muted small">{rows.length.toLocaleString('en-IE')} {rows.length === 1 ? noun[0] : noun[1]}{select && select.picked.size > 0 ? ` · ${select.picked.size} ticked` : ''}</span>
        <div className="grid-tools">
          <div className="grid-pick" ref={pickRef}>
            <button type="button" className="button ghost small" aria-expanded={picking} onClick={() => setPicking(!picking)}>Columns</button>
            {picking && (
              <div className="grid-pop" role="group" aria-label="Columns to show">
                {columns.map((c) => (
                  <label key={c.key} className="check">
                    <input type="checkbox" checked={!hidden.has(c.key)} disabled={c.always}
                      onChange={() => setHidden((h) => { const n = new Set(h); n.has(c.key) ? n.delete(c.key) : n.add(c.key); return n; })} />
                    {c.label}
                  </label>
                ))}
                <button type="button" className="link small" onClick={() => setHidden(new Set(columns.filter((c) => c.hidden).map((c) => c.key)))}>Reset</button>
              </div>
            )}
          </div>
          <button type="button" className="button ghost small" onClick={downloadCsv} disabled={rows.length === 0}>Download CSV</button>
        </div>
      </div>
      {rows.length === 0 ? <div className="grid-empty muted">{empty ?? `No ${noun[1]}.`}</div> : (
        <div className="grid-scroll" tabIndex={0} aria-label={`${noun[1]} table`}>
          <table className={`grid${href ? ' clickable' : ''}`}>
            <thead>
              <tr>
                {select && (
                  <th className="pick"><input type="checkbox" aria-label="Tick all" checked={allPicked}
                    onChange={() => select.onChange(allPicked ? new Set() : new Set(rows.map((r) => rowKey(r) as number)))} /></th>
                )}
                {cols.map((c) => (
                  <th key={c.key} className={c.align === 'right' ? 'num' : undefined}
                    aria-sort={sort?.key === c.key ? (sort.dir === 1 ? 'ascending' : 'descending') : undefined}>
                    {c.nosort ? c.label : (
                      <button type="button" className="grid-sort" onClick={() => clickSort(c)}>
                        {c.label}<span className="grid-arrow" aria-hidden="true">{sort?.key === c.key ? (sort.dir === 1 ? '▲' : '▼') : ''}</span>
                      </button>
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sorted.map((r) => {
                const k = rowKey(r);
                const extra = after?.(r);
                return (
                  <Fragment key={k}>
                    <tr className={rowClass?.(r)} onClick={href ? (e) => {
                      if (!(e.target as HTMLElement).closest('a, input, button, label, .pick')) navigate(href(r), { state: rowState });
                    } : undefined}>
                      {select && (
                        <td className="pick"><input type="checkbox" aria-label={`Tick ${select.label(r)}`} checked={select.picked.has(k as number)}
                          onChange={() => { const n = new Set(select.picked); n.has(k as number) ? n.delete(k as number) : n.add(k as number); select.onChange(n); }} /></td>
                      )}
                      {cols.map((c) => (
                        <td key={c.key} className={[c.align === 'right' && 'num', c.nowrap && 'nowrap'].filter(Boolean).join(' ') || undefined}>
                          {c.cell ? c.cell(r) : (c.value(r) ?? <span className="muted">–</span>)}
                        </td>
                      ))}
                    </tr>
                    {extra && <tr className="grid-after"><td colSpan={colCount}>{extra}</td></tr>}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
